import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ProjectItemSkeleton from './ProjectItemSkeleton';
import ErrorState from './../../ErrorState/ErrorState';
import { AnimatedOnScroll } from '../../AnimatedOnScroll';
import { IMAGES_BASE_URL } from '../../../config/constants';
import './ProjectItem.scss';

const generateSlug = (title) => {
  if (!title) return '';
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');
};

const processProjectData = (project) => {
  if (!project) return null;

  const titleText = typeof project.mainTitle === 'object' 
    ? project.mainTitle.en || Object.values(project.mainTitle)[0] 
    : project.mainTitle;
  
  const slug = generateSlug(titleText);
  
  return {
    ...project,
    button: {
      text: {
        ua: 'Переглянути проект',
        en: 'View Project',
        ru: 'Посмотреть проект',
        pl: 'Zobacz projekt',
        de: 'Projekt ansehen',
        es: 'Ver proyecto',
      },
      link: `/work/${slug}`,
    }
  };
};

const ProjectItem = ({ project, index }) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;

  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [projectData, setProjectData] = useState(null);

  const isRight = useMemo(() => index % 2 === 0, [index]);
  const positionClass = useMemo(() => isRight ? 'right' : 'left', [isRight]);

  const getLocalizedText = useCallback((text) => {
    if (!text) return '';
    if (typeof text === 'object') {
      return text[currentLang] || text.en || '';
    }
    return text;
  }, [currentLang]);

  const getWorkListItems = useCallback((workList) => {
    if (!workList) return [];
    
    if (workList.type === 'list' && workList.items) {
      return workList.items.map(item => getLocalizedText(item));
    }
    
    if (workList[currentLang]) {
      return workList[currentLang];
    }
    
    return workList.en || [];
  }, [currentLang, getLocalizedText]);

  const isValidProject = useMemo(() => 
    project && 
    project.mainTitle && 
    project.bgImage && 
    project.projectImage && 
    project.subtitle && 
    project.workList,
    [project]
  );

  useEffect(() => {
    if (!project) {
      setHasError(true);
      setIsLoading(false);
      return;
    }

    const processProject = () => {
      try {
        setHasError(false);
        
        if (isValidProject) {
          const processedProject = processProjectData(project);
          setProjectData(processedProject);
        } else {
          setHasError(true);
          setProjectData(null);
        }
      } catch (error) {
        console.error('Error processing project:', error);
        setHasError(true);
        setProjectData(null);
      } finally {
        setIsLoading(false);
      }
    };

    processProject();
  }, [project, isValidProject]);

  const handleRetry = useCallback(() => {
    setHasError(false);
    setIsLoading(true);
    
    setTimeout(() => {
      if (isValidProject) {
        const processedProject = processProjectData(project);
        setProjectData(processedProject);
        setHasError(false);
      } else {
        setProjectData(null);
        setHasError(true);
      }
      setIsLoading(false);
    }, 0);
  }, [project, isValidProject]);

  const renderData = useMemo(() => {
    if (!projectData) return null;

    const projectSlug = generateSlug(getLocalizedText(projectData.mainTitle));
    const workListItems = getWorkListItems(projectData.workList);
    const buttonText = getLocalizedText(projectData.button?.text);
    const mainTitle = getLocalizedText(projectData.mainTitle);
    const subtitle = getLocalizedText(projectData.subtitle);
    const bgImage = getLocalizedText(projectData.bgImage);
    const projectImage = getLocalizedText(projectData.projectImage);

    return {
      projectSlug,
      workListItems,
      buttonText,
      mainTitle,
      subtitle,
      bgImage: `${IMAGES_BASE_URL}${bgImage}`,
      projectImage: `${IMAGES_BASE_URL}${projectImage}`,
      altText: `${mainTitle} ${positionClass}`
    };
  }, [projectData, getLocalizedText, getWorkListItems, positionClass]);

  const handleImageError = useCallback((e) => {
    e.target.style.display = 'none';
  }, []);

  if (!project) {
    return <ProjectItemSkeleton position={positionClass} />;
  }

  if (isLoading) {
    return <ProjectItemSkeleton position={positionClass} />;
  }

  if (hasError || !projectData || !renderData) {
    return <ErrorState onRetry={handleRetry} />;
  }

  const {
    projectSlug,
    workListItems,
    buttonText,
    mainTitle,
    subtitle,
    bgImage,
    projectImage,
    altText
  } = renderData;

  return (
    <div className="pro__items">
      <div key={projectData.id} className={`project__item ${positionClass}`}>
        <div
          className="project__item__bg__image"
          style={{
            backgroundImage: `url(${bgImage})`,
          }}
        >
          <AnimatedOnScroll 
            animation="fade-down"
            duration={800}
            delay={0}
            className="project__item__image"
          >
            <img
              src={projectImage}
              alt={altText}
              className="project__item__pic"
              onError={handleImageError}
              loading="lazy"
            />
          </AnimatedOnScroll>
        </div>
        <div className="project__item__container">
          <div className="project__item__content">
            <AnimatedOnScroll animation="fade-up" delay={0}>
            <h4 className="project__item__title">
              {mainTitle}
            </h4>
            </AnimatedOnScroll>
            <AnimatedOnScroll animation="fade-up" delay={100}>
            <h5 className="project__item__subtitle">
              {subtitle}
            </h5>
            </AnimatedOnScroll>
            {workListItems.length > 0 && (
              <ul className="project__list">
                <AnimatedOnScroll animation="fade-up" delay={100}>
                {workListItems.map((item, i) => (
                  <li key={i} className="project__list__item">
                    {item}
                  </li>
                ))}
                </AnimatedOnScroll>
              </ul>
            )}
            <AnimatedOnScroll animation="fade-up" delay={100}>
            <Link 
              to={`/work/${projectSlug}`} 
              className="project__item__button"
            >
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              {buttonText}
            </Link>
            </AnimatedOnScroll>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(ProjectItem);